import type {
  ApiResponse,
  CircuitPreferences,
  CreateSavedTrackRequest,
  SavedTrack,
  TelemetrySessionContext,
  UpdateTelemetrySessionContextRequest,
  UpdateCircuitPreferencesRequest,
} from '@kangaroo/shared';

import { api } from '../lib/api';

function extractApiErrorMessage(error: unknown, fallback: string) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
  ) {
    return (error as { response: { data: { message: string } } }).response.data.message;
  }

  if (error instanceof Error && !error.message.startsWith('Request failed with status code')) {
    return error.message;
  }

  return fallback;
}

export async function fetchTelemetryTracks() {
  const response = await api.get<ApiResponse<SavedTrack[]>>('/telemetry/tracks');
  return response.data.data;
}

export async function createTelemetryTrack(payload: CreateSavedTrackRequest) {
  const response = await api.post<ApiResponse<SavedTrack>>('/telemetry/tracks', payload);
  return response.data.data;
}

export async function updateTelemetryTrack(trackId: string, payload: Partial<CreateSavedTrackRequest>) {
  const response = await api.put<ApiResponse<SavedTrack>>(
    `/telemetry/tracks/${encodeURIComponent(trackId)}`,
    payload,
  );
  return response.data.data;
}

/** Update an existing team track when present; otherwise match by name or create. */
export async function resolveTelemetryTrackForSave(
  preferredTrackId: string | null | undefined,
  payload: CreateSavedTrackRequest,
): Promise<SavedTrack> {
  const tracks = await fetchTelemetryTracks();
  const normalizedName = payload.name.trim().toLowerCase();

  if (preferredTrackId && tracks.some((track) => track.id === preferredTrackId)) {
    return updateTelemetryTrack(preferredTrackId, payload);
  }

  const byName = tracks.find((track) => track.name.trim().toLowerCase() === normalizedName);
  if (byName) {
    return updateTelemetryTrack(byName.id, payload);
  }

  return createTelemetryTrack(payload);
}

export async function fetchCircuitPreferences() {
  const response = await api.get<ApiResponse<CircuitPreferences>>('/telemetry/circuit-preferences');
  return response.data.data;
}

export async function updateCircuitPreferences(payload: UpdateCircuitPreferencesRequest) {
  const response = await api.put<ApiResponse<CircuitPreferences>>('/telemetry/circuit-preferences', payload);
  return response.data.data;
}

export async function fetchTelemetrySessionContext(sessionKey: string) {
  const response = await api.get<ApiResponse<TelemetrySessionContext>>(`/telemetry/sessions/${encodeURIComponent(sessionKey)}/context`);
  return response.data.data;
}

export async function fetchTelemetrySessionContexts() {
  const response = await api.get<ApiResponse<TelemetrySessionContext[]>>('/telemetry/sessions/contexts');
  return response.data.data;
}

export async function updateTelemetrySessionContext(
  sessionKey: string,
  payload: UpdateTelemetrySessionContextRequest,
) {
  try {
    const response = await api.put<ApiResponse<TelemetrySessionContext>>(
      `/telemetry/sessions/${encodeURIComponent(sessionKey)}/context`,
      payload,
    );
    return response.data.data;
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Could not save session details.'));
  }
}

export const telemetryTracksKey = ['telemetry', 'tracks'] as const;
export const telemetrySessionContextsKey = ['telemetry', 'sessions', 'contexts'] as const;
export const circuitPreferencesKey = ['telemetry', 'circuit-preferences'] as const;

export function telemetrySessionContextKey(sessionKey: string) {
  return ['telemetry', 'sessions', sessionKey, 'context'] as const;
}
